import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Action, NgxsOnInit, Selector, State, StateContext, Store } from '@ngxs/store';
import { Chance } from 'chance';
import _ from 'lodash';
import moment from 'moment';
import { of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, switchMap, tap } from 'rxjs/operators';
import { NotificationType } from 'src/app/samwebDescription/samweb/notification/notificationType';
import { FeatureEnum } from 'src/app/samwebDescription/samwin/FeatureEnum';
import { LineStateEnum } from 'src/app/samwebDescription/samwin/LineStateEnum';
import { QueueSelectionPurpose } from 'src/app/samwebDescription/samwin/QueueSelectionPurpose';
import { AssistanceState } from 'src/generated/signalR/assistance-state';
import { CallRecordingState } from 'src/generated/signalR/call-recording-state';
import { IciFlags } from 'src/generated/signalR/ici-flags';
import { SamwinCallInfoRemoteDto } from 'src/generated/signalR/samwin-call-info-remote-dto';
import { SkillConfigurationRemoteDto } from 'src/generated/signalR/skill-configuration-remote-dto';
import { CallsService } from 'src/generated/swagger';
import { CurrentNumberChanged } from '../../dashboard/brick-wall/brick-behaviour-state/brickTypes/call-control-behaviour/call-control-behaviour.actions';
import { CallControlBehaviourState } from '../../dashboard/brick-wall/brick-behaviour-state/brickTypes/call-control-behaviour/call-control-behaviour.state';
import { ToastUserInfoNotification } from '../../notification/notification-toastr/notification-toastr.actions';
import { EnabledFeaturesState } from '../../session/enabled-features/enabled-features.state';
import { SessionState } from '../../session/session.state';
import { CallTagsSelectors } from '../call-tags/call-tags.selectors';
import { CurrentDialTargetChanged } from '../current-dial-target/current-dial-target.actions';
import { NextDialTargetChanged } from '../next-dial-target/next-dial-target.actions';
import { NextDialTargetState } from '../next-dial-target/next-dial-target.state';
import { StartQueueSelectionProcess } from '../queue-selection-dialog/queue-selection-dialog.actions';
import { SamwinAcdExtensionStateSelectors } from '../samwin-acd-extension/samwin-acd-extension.selectors';
import { SamwinCallListState } from '../samwin-call-list/samwin-call-list.state';
import { SamwinLinesStateSelector } from '../samwin-lines/samwin-lines.selector';
import { IShortCallInfo, SamwinLinesState } from '../samwin-lines/samwin-lines.state';
import { SamwinMaliciousCallMailConfigState } from '../samwin-malicious-call-mail-config/samwin-malicious-call-mail-config.state';
import { SamwinOutboundSkillShortInfoState } from '../samwin-outbound-skill-shortinfo/samwin-outbound-skill-shortinfo.state';
import { SamwinSkillsState } from '../samwin-skills/samwin-skills.state';
import { UpdateDesiredAssistanceState } from '../samwin-user-assistance-status/samwin-user-assistance-status.actions';
import { SamwinUserAssistanceState } from '../samwin-user-assistance-status/samwin-user-assistance-status.state';
import { SamwinUserStatusState } from '../samwin-user-status/samwin-user-status.state';
import {
   AcceptCallFromCallList,
   AcceptOrStartCallAction,
   AcceptRingingCallAction,
   AcceptSpecificCallAction,
   BargeInConference,
   BargeInListen,
   BargeInWhisper,
   CallRecordingStateUpdatedByServer,
   ChangeRequestUserDecision,
   CompleteTransferAction,
   DialpadKeyPressedAction,
   DialpadNumberChangedAction,
   DisconnectLineA,
   DisconnectLineB,
   EndCallAction,
   ExecuteIvr,
   InitiateNewCallAction,
   InitiateNewConsultCallAction,
   MarkMaliciousCall,
   ParkCallAgent,
   ParkCallSkill,
   PhoneStartedRinging,
   ReQueueCall,
   ReassignCall,
   SendDtmfToneAction,
   SendDtmfToneInternalAction,
   SourceLineConnected,
   StartConference,
   StartNewCallAction,
   SwitchHoldLines,
   ToggleHoldLineA,
   ToggleHoldLineB,
   ToggleManualCallRecording,
} from './client-phone.actions';

export interface ClientPhoneStateModel {
   currentCallInitialisationId: string;
   browserTabId: string;
   numberToDtmf: string[];
   ringingStartTime: moment.Moment;
   connectedTime: moment.Moment;
   userDecisionRequested: boolean;
   recordingState: CallRecordingState;
}

@State<ClientPhoneStateModel>({
   name: 'clientPhone',
   defaults: {
      currentCallInitialisationId: '',
      browserTabId: new Chance().string({ length: 8, casing: 'lower', alpha: true, numeric: true }),
      numberToDtmf: [],
      ringingStartTime: moment.min(),
      connectedTime: null,
      userDecisionRequested: false,
      recordingState: CallRecordingState.NotRecording,
   },
   children: [NextDialTargetState],
})
@Injectable()
export class ClientPhoneState implements NgxsOnInit {
   constructor(private store: Store, private callsService: CallsService, private translateService: TranslateService) {
      this.store
         .select(SamwinLinesStateSelector.isPhoneRinging)
         .pipe(
            distinctUntilChanged(),
            filter((isPhoneRinging) => isPhoneRinging)
         )
         .subscribe(() => this.store.dispatch(new PhoneStartedRinging()));
   }

   @Selector([ClientPhoneState])
   public static getRingingStartTime(state: ClientPhoneStateModel) {
      return state.ringingStartTime;
   }

   @Selector([ClientPhoneState])
   public static getConnectedTime(state: ClientPhoneStateModel) {
      return state.connectedTime;
   }

   @Selector([
      ClientPhoneState.isCallRingingAndAcceptable,
      ClientPhoneState.getRingingStartTime,
      NextDialTargetState.nextDialTargetChangedAt,
      NextDialTargetState.nextDialTargetNumber,
      SamwinLinesStateSelector.getCurrentCallShortInfo,
   ])
   public static getAcceptOrStartCallAction(
      isCallRingingAndAcceptable: boolean,
      ringingStartTime: moment.Moment,
      dialTargetChangedAt: moment.Moment,
      nextDialTargetNumber: string,
      ringingCall: IShortCallInfo
   ): 'accept' | 'dial' | 'user-decision' | 'none' {
      if (isCallRingingAndAcceptable) {
         // if no new dial target was published (or there isn't one) since the phone started ringing, we accept
         if (dialTargetChangedAt < ringingStartTime || nextDialTargetNumber === ringingCall?.peerId) {
            return 'accept';
         }

         return _.isEmpty(nextDialTargetNumber) ? 'accept' : 'user-decision';
      }

      return _.isEmpty(nextDialTargetNumber) ? 'none' : 'dial';
   }

   @Selector([ClientPhoneState, ClientPhoneState.getAcceptOrStartCallAction])
   public static isAcceptOrStartCallDecisionRequired(
      state: ClientPhoneStateModel,
      acceptOrStartCallAction: 'accept' | 'dial' | 'user-decision' | 'none'
   ): boolean {
      return state.userDecisionRequested && acceptOrStartCallAction === 'user-decision';
   }

   @Selector([
      SamwinLinesState.getActiveOrRingingCallId,
      SamwinLinesStateSelector.getSourceLineState,
      SamwinCallListState.getCallByIdFn,
      SamwinSkillsState.getSkillConfigurationFn,
      SamwinCallListState.getCallInfoFromPreviousCall,
      SamwinAcdExtensionStateSelectors.getCurrentAcdSkillID,
   ])
   public static getCurrentSkillUrl(
      activeCallId: number,
      sourceLineState: LineStateEnum,
      getCallByIdFn: (callId: number) => SamwinCallInfoRemoteDto,
      getSkillConfigurationFn: (skillid: number) => SkillConfigurationRemoteDto,
      previousCallInfo: SamwinCallInfoRemoteDto,
      defaultSkillID: number
   ) {
      // Skill url refresh not implemented, because its a niche feature. We implement it when needed.
      // skillUrlRefresh => Web application URL loaded when refreshAgentURL is called in lua script
      let callInfo: SamwinCallInfoRemoteDto;
      let skillConfig: SkillConfigurationRemoteDto;
      if (!_.isNil(activeCallId)) {
         callInfo = getCallByIdFn(activeCallId);
      } else {
         callInfo = previousCallInfo;
      }
      if (_.isNil(callInfo)) {
         // If there is no callInfo but an activeCallId we do probably not have all information yet.
         // Therefore return "null" to continue observable (e.g. in samwin.state.ts)
         if (!_.isNil(activeCallId)) {
            return null;
         }
         skillConfig = getSkillConfigurationFn(defaultSkillID);
         return skillConfig ? skillConfig.skillUrlStartup : null;
      }
      skillConfig = getSkillConfigurationFn(callInfo?.queueId);
      if (_.isNil(skillConfig)) {
         return '';
      }
      // inbound Call LifeCycle ==> IDLE => ACCEPTED => CONNECTED => IDLE
      // outbound Call LifeCycle ==> IDLE => RINGBACK => CONNECTED => IDLE

      // trigger only on change
      switch (sourceLineState) {
         case LineStateEnum.ACCEPTED:
            return skillConfig.skillUrl;
         case LineStateEnum.RINGBACK:
            return skillConfig.skillUrlRingback;
         case LineStateEnum.CONNECTED:
            if (callInfo.ici.ici & IciFlags.INBOUND) {
               return skillConfig.skillUrlConnected;
            }
            if (callInfo.ici.ici & IciFlags.OUTBOUND) {
               return skillConfig.skillUrlConnectedOutbound;
            }
            break;
         case LineStateEnum.IDLE:
            return skillConfig.skillUrlDisconnected;
      }
      return '';
   }

   @Selector([SamwinLinesState.getActiveOrRingingCallId, SamwinCallListState.getCallByIdFn])
   public static getWaitingSinceActiveCall(activeCallId: number, getCallByIdFn: (callId: number) => SamwinCallInfoRemoteDto) {
      if (!_.isNil(activeCallId)) {
         return getCallByIdFn(activeCallId).waitingSince;
      } else {
         return null;
      }
   }

   @Selector([SamwinUserStatusState.isAgentActive, NextDialTargetState.nextDialTargetNumber])
   public static canPhoneMakeCall(isAgentActive: boolean, nextDialTargetNumber: string): boolean {
      return isAgentActive && !_.isEmpty(nextDialTargetNumber);
   }

   @Selector([SamwinUserStatusState.isAgentActive, SamwinLinesStateSelector.getDestinationLineState])
   public static canAddCall(isAgentActive: boolean, destinationLineState: LineStateEnum): boolean {
      return isAgentActive && destinationLineState === LineStateEnum.IDLE;
   }

   @Selector([ClientPhoneState.canPhoneMakeCall, ClientPhoneState.canAddCall])
   public static canMakeCall(canPhoneMakeCall: boolean, canAddCall: boolean): boolean {
      return canPhoneMakeCall && canAddCall;
   }

   // This selector does not care whether or not a Next Dial Target is set
   // (to be used in cases where a number is provided).
   @Selector([SamwinUserStatusState.isAgentActive, ClientPhoneState.canAddCall])
   public static canMakeCallForNumber(isAgentActive: boolean, canAddCall: boolean): boolean {
      return isAgentActive && canAddCall;
   }

   @Selector([SamwinAcdExtensionStateSelectors.canAcdExtensionAcceptCall, SamwinLinesStateSelector.isCallInProgress, SamwinLinesStateSelector.isPhoneRinging])
   public static isCallRingingAndAcceptable(canAcdExtensionAcceptCall: boolean, isCallInProgress: boolean, isPhoneRinging: boolean): boolean {
      return canAcdExtensionAcceptCall && isPhoneRinging && !isCallInProgress;
   }

   @Selector([ClientPhoneState])
   public static currentCallInitialisationId(state: ClientPhoneStateModel): string {
      return state.currentCallInitialisationId;
   }

   @Selector([SamwinLinesStateSelector.getSourceLineState])
   public static isLineAIdle(sourceLineState: LineStateEnum): boolean {
      return sourceLineState === LineStateEnum.IDLE;
   }

   @Selector([SamwinLinesStateSelector.getDestinationLineState])
   public static isLineBIdle(destinationLineState: LineStateEnum): boolean {
      return destinationLineState === LineStateEnum.IDLE;
   }

   @Selector([ClientPhoneState])
   public static getBrowserTabId(state: ClientPhoneStateModel): string {
      return state.browserTabId;
   }

   @Selector([ClientPhoneState])
   public static getRecordingState(state: ClientPhoneStateModel): CallRecordingState {
      return state.recordingState;
   }

   @Selector([ClientPhoneState])
   public static isRecordingOrError(state: ClientPhoneStateModel): boolean {
      return state.recordingState === CallRecordingState.Recording || state.recordingState === CallRecordingState.Error;
   }

   ngxsOnInit() {
      this.store
         .select(SamwinLinesStateSelector.getSourceLineState)
         .pipe(
            tap((sourceLineState) => {
               switch (sourceLineState) {
                  case LineStateEnum.IDLE:
                     this.store.dispatch(new SourceLineConnected(LineStateEnum.IDLE));
                     break;
                  case LineStateEnum.ACCEPTED:
                  case LineStateEnum.RINGBACK:
                     break;
                  case LineStateEnum.CONNECTED:
                     this.store.dispatch(new SourceLineConnected(LineStateEnum.CONNECTED));
                     break;
               }
            })
         )
         .subscribe();
   }

   @Action(DialpadKeyPressedAction)
   public DialpadKeyPressedAction(ctx: StateContext<ClientPhoneStateModel>, action: DialpadKeyPressedAction) {
      const prevNumber = this.store.selectSnapshot(CallControlBehaviourState.getCurrentNumberFn)(action.brickInstanceId);
      const newNumber: string = (prevNumber ?? '') + action.keyValue;
      ctx.dispatch(new SendDtmfToneAction(action.keyValue));
      return ctx.dispatch(new CurrentNumberChanged(newNumber, action.brickInstanceId));
   }

   @Action(SendDtmfToneAction)
   public sendDTMFToneAction(ctx: StateContext<ClientPhoneStateModel>, action: SendDtmfToneAction) {
      const isCallInProgress: boolean = this.store.selectSnapshot(SamwinLinesStateSelector.isCallInProgress);
      if (!isCallInProgress || !'0123456789*#'.includes(action.pressedDialpadKey)) {
         return;
      }

      const numbersToDtmf = [...ctx.getState().numberToDtmf, action.pressedDialpadKey];
      ctx.patchState({
         numberToDtmf: numbersToDtmf,
      });

      if (numbersToDtmf.length === 1) {
         ctx.dispatch(new SendDtmfToneInternalAction());
      }
   }

   @Action(SendDtmfToneInternalAction)
   public sendDTMFToneInternalAction(ctx: StateContext<ClientPhoneStateModel>, action: SendDtmfToneInternalAction) {
      const numbersToDtmf = ctx.getState().numberToDtmf;
      if (_.isEmpty(numbersToDtmf)) {
         return;
      }

      const numberForDtmfTone = _.head(numbersToDtmf);
      return this.callsService.sendDTMFTone({ digit: numberForDtmfTone }).pipe(
         catchError((x) => {
            console.error('Error during sendDtmfTone', x);
            return null;
         }),
         tap(() => {
            ctx.patchState({
               numberToDtmf: ctx.getState().numberToDtmf.slice(1),
            });
            this.store.dispatch(new SendDtmfToneInternalAction());
         })
      );
   }

   @Action(DialpadNumberChangedAction)
   public DialpadNumberChangedAction(ctx: StateContext<ClientPhoneStateModel>, action: DialpadNumberChangedAction) {
      if (action.processInputForDtmf) {
         // send DTMF digits for newly added digits or all digits if the number in dial pad got replaced
         const currentNumber = this.store.selectSnapshot(CallControlBehaviourState.getCurrentNumberFn)(action.brickInstanceId);
         const digitsToProcess = _.startsWith(action.dialpadNumber, currentNumber) ? action.dialpadNumber.substr(currentNumber.length) : action.dialpadNumber;
         _.forEach(digitsToProcess, (digit) => ctx.dispatch(new SendDtmfToneAction(digit)));
      }
      ctx.dispatch(new NextDialTargetChanged(action.dialpadNumber, action.brickInstanceId));
   }

   @Action(AcceptOrStartCallAction)
   public acceptOrStartCallAction(ctx: StateContext<ClientPhoneStateModel>, action: AcceptOrStartCallAction) {
      const acceptOrStartCallAction = this.store.selectSnapshot(ClientPhoneState.getAcceptOrStartCallAction);
      if (acceptOrStartCallAction === 'accept' || action.userDecision === 'accept') {
         this.store.dispatch(new ChangeRequestUserDecision(false));

         return ctx.dispatch(new AcceptRingingCallAction());
      }

      if (acceptOrStartCallAction === 'dial' || action.userDecision === 'dial') {
         this.store.dispatch(new ChangeRequestUserDecision(false));

         const nextDialTarget = this.store.selectSnapshot(NextDialTargetState.nextDialTarget);
         const isCallInProgress = this.store.selectSnapshot(SamwinLinesStateSelector.isCallInProgress);
         if (isCallInProgress) {
            return ctx.dispatch(new InitiateNewConsultCallAction(nextDialTarget));
         }

         return ctx.dispatch(new StartNewCallAction(nextDialTarget));
      }

      if (acceptOrStartCallAction === 'user-decision') {
         return ctx.dispatch(new ChangeRequestUserDecision(true));
      }

      // nothing to do if acceptOrStartCallAction === 'none'
   }

   @Action(AcceptRingingCallAction)
   public AcceptCallAction(ctx: StateContext<ClientPhoneStateModel>, action: AcceptRingingCallAction) {
      const ringingCallId = this.store.selectSnapshot(SamwinLinesStateSelector.getRingingCallId);
      if (_.isNil(ringingCallId)) {
         console.log(`No ringing call available to accept`);
         return of(null);
      }

      return ctx.dispatch(new AcceptSpecificCallAction(ringingCallId));
   }

   @Action(AcceptSpecificCallAction)
   public AcceptSpecificCallAction(ctx: StateContext<ClientPhoneStateModel>, action: AcceptSpecificCallAction) {
      console.log(`Accept call ${action.callId}`);
      return this.callsService.acceptCall({ callId: action.callId, callInitiatorId: ctx.getState().browserTabId });
   }

   @Action(AcceptCallFromCallList)
   public AcceptCallFromCallList(ctx: StateContext<ClientPhoneStateModel>, action: AcceptCallFromCallList) {
      if (_.isNil(action.call)) {
         console.warn(`Can't pick null call`);
         return;
      }
      if (!this.store.selectSnapshot(ClientPhoneState.canAddCall)) {
         console.log(`Can't pick call due to ClientPhoneState`);
         return;
      }

      let canPickCall = false;
      if (action.position === 1) {
         // Calls on position 1 can always be picked
         canPickCall = true;
      } else {
         // Calls on positions > 1 can be picked if the related Feature is granted or they are parked calls
         canPickCall =
            this.store.selectSnapshot(EnabledFeaturesState.isFeatureEnabled(FeatureEnum.CCO_PERM_PICK_CALL_FROM_CALL_LIST)) ||
            (action.call.ici.ici & IciFlags.PARK_PRIVATE) === IciFlags.PARK_PRIVATE ||
            (action.call.ici.ici & IciFlags.PARK_PUBLIC) === IciFlags.PARK_PUBLIC;
      }

      if (!canPickCall) {
         console.log(`Pick call not allowed for call on position ${action.position}`);
         return;
      }

      return ctx.dispatch(new AcceptSpecificCallAction(action.call.callId));
   }

   @Action(StartNewCallAction)
   public StartNewCallAction(ctx: StateContext<ClientPhoneStateModel>, action: StartNewCallAction) {
      if (_.isEmpty(action.numberToDial)) {
         console.warn(`Can't start call with empty number`);
         return;
      }
      if (!this.store.selectSnapshot(ClientPhoneState.canMakeCallForNumber)) {
         console.warn(`Can't start call due to current ClientPhoneState`);
         return;
      }

      if (typeof action.numberToDial !== 'string') {
         action.numberToDial = action.numberToDial.number;
      }

      console.log(`Start new call with number ${action.numberToDial}`);

      if (this.store.selectSnapshot(SamwinLinesStateSelector.isCallInProgress)) {
         return ctx.dispatch(new InitiateNewConsultCallAction(action.numberToDial));
      }

      // valid skill provided?
      if (!_.isNil(action.skillId) && action.skillId !== 0) {
         return ctx.dispatch(new InitiateNewCallAction(action.numberToDial, action.skillId));
      }

      const skillCount: number = this.store.selectSnapshot(SamwinOutboundSkillShortInfoState.getSkillCount);
      if (skillCount > 1) {
         return ctx.dispatch(new StartQueueSelectionProcess(QueueSelectionPurpose.OUTBOUND_SKILL, action));
      }
      if (skillCount === 1) {
         const skillId: number = this.store.selectSnapshot(SamwinOutboundSkillShortInfoState.getSingleSkillId);
         return ctx.dispatch(new InitiateNewCallAction(action.numberToDial, skillId));
      }
      if (skillCount === 0) {
         return ctx.dispatch(new InitiateNewCallAction(action.numberToDial, null));
      }
   }

   @Action(InitiateNewCallAction)
   public InitiateNewCallAction(ctx: StateContext<ClientPhoneStateModel>, action: InitiateNewCallAction) {
      let numberToDial = '';
      let subscriberId: number = null;
      if (typeof action.numberToDial === 'string') {
         numberToDial = action.numberToDial;
      } else {
         numberToDial = action.numberToDial?.number;
         subscriberId = action.numberToDial?.subscriberId;
      }

      this.store.dispatch(new CurrentDialTargetChanged(numberToDial));

      return this.callsService.startNewCall({
         numberToDial: numberToDial,
         callInitiatorId: ctx.getState().browserTabId,
         skillId: action.skillId === 0 ? null : action.skillId,
         subscriberId: subscriberId === 0 ? null : subscriberId,
      });
   }

   @Action(InitiateNewConsultCallAction)
   public InitiateNewConsultCallAction(ctx: StateContext<ClientPhoneStateModel>, action: InitiateNewConsultCallAction) {
      let numberToDial = '';
      let subscriberId: number = null;
      if (typeof action.numberToDial === 'string') {
         numberToDial = action.numberToDial;
      } else {
         numberToDial = action.numberToDial?.number;
         subscriberId = action.numberToDial?.subscriberId;
      }

      this.store.dispatch(new CurrentDialTargetChanged(numberToDial));

      return this.callsService.startNewCall({
         numberToDial: numberToDial,
         callInitiatorId: ctx.getState().browserTabId,
         subscriberId: subscriberId === 0 ? null : subscriberId,
      });
   }

   @Action(EndCallAction)
   public hangUpCallAction(ctx: StateContext<ClientPhoneStateModel>, action: EndCallAction) {
      console.log(`End call`);

      if (this.store.selectSnapshot(SamwinLinesStateSelector.canDisconnectLineB)) {
         // line B can be disconnected (implying that line B is not idle)
         // line A is also not idle
         return this.store.dispatch(new CompleteTransferAction());
      }

      return this.store.dispatch(new DisconnectLineA());
   }

   // as a preparation when we once have a user action with configurable no-answer-time duration
   @Action(CompleteTransferAction)
   public completeTransfer(ctx: StateContext<ClientPhoneStateModel>, action: CompleteTransferAction) {
      return this.callsService.completeTransfer();
   }

   @Action(DisconnectLineA)
   public disconnectLineA(ctx: StateContext<ClientPhoneStateModel>, action: DisconnectLineA) {
      ctx.patchState({
         connectedTime: null,
      });
      return this.callsService.disconnectLineA();
   }

   @Action(DisconnectLineB)
   public disconnectLineB(ctx: StateContext<ClientPhoneStateModel>, action: DisconnectLineB) {
      return this.callsService.disconnectLineB();
   }

   @Action(ReassignCall)
   public ReassignCall(ctx: StateContext<ClientPhoneStateModel>, action: ReassignCall) {
      const ringingCallId: number = this.store.selectSnapshot(SamwinLinesStateSelector.getRingingCallId);

      if (_.isNil(ringingCallId)) {
         console.log(`No ringing call with id ${ringingCallId} to reassign!`);
         return of(null);
      }

      console.log(`Reassign call ${ringingCallId}`);

      return this.callsService.reassignCall({ callIdToReassign: ringingCallId });
   }

   @Action(ParkCallAgent)
   public parkCallAgent(ctx: StateContext<ClientPhoneStateModel>, action: ParkCallAgent) {
      const currentCall = this.store.selectSnapshot(SamwinLinesStateSelector.getCurrentCallShortInfo);

      if (_.isNil(currentCall)) {
         console.log(`No currentCall to park!`);
         return of(null);
      }

      console.log(`Agent park call ${currentCall.callId}`);

      return this.callsService.parkCallAgent({ callIdToPark: currentCall.callId });
   }

   @Action(ParkCallSkill)
   public parkCallSkill(ctx: StateContext<ClientPhoneStateModel>, action: ParkCallSkill) {
      const currentCall = this.store.selectSnapshot(SamwinLinesStateSelector.getCurrentCallShortInfo);

      if (_.isNil(currentCall)) {
         console.log(`No currentCall to park!`);
         return of(null);
      }

      console.log(`Agent park call ${currentCall.callId}`);

      return this.callsService.parkCallSkill({ callIdToPark: currentCall.callId });
   }

   @Action(StartConference)
   public startConference(ctx: StateContext<ClientPhoneStateModel>, action: StartConference) {
      const currentCall = this.store.selectSnapshot(SamwinLinesStateSelector.getCurrentCallShortInfo);

      if (_.isNil(currentCall)) {
         console.log(`No current call to start conference!`);
         return of(null);
      }

      return this.callsService.startConference();
   }

   @Action(ReQueueCall)
   public requeueCall(ctx: StateContext<ClientPhoneStateModel>, action: ReQueueCall) {
      console.log(`Re-Queue call ${action.callId} to queue ${action.queueId}`);
      return this.callsService.reQueueCall({ callId: action.callId, queueId: action.queueId });
   }

   @Action(ExecuteIvr)
   public executeIvr(ctx: StateContext<ClientPhoneStateModel>, action: ExecuteIvr) {
      console.log(`Execute IVR ${action.ivrId} on call ${action.callId}`);
      return this.callsService.executeIvr({ callId: action.callId, ivrId: action.ivrId });
   }

   @Action(ToggleManualCallRecording)
   public toggleManualCallRecording(ctx: StateContext<ClientPhoneStateModel>, action: ToggleManualCallRecording) {
      if (!this.store.selectSnapshot(SamwinLinesStateSelector.canToggleManualCallRecording)) {
         console.debug('Toggling manual call recording is currently not possible');
         return;
      }

      const callId = this.store.selectSnapshot(SamwinLinesStateSelector.getCurrentCallShortInfo)?.callId;
      if (_.isNil(callId)) {
         console.debug('Toggling manual call recording is currently not possible, no call available');
         return;
      }

      const startRecording = !this.store.selectSnapshot(SamwinLinesStateSelector.isCallBeingRecorded);
      console.log(`Toggle manual call recording of call ${callId}, start recording: ${startRecording}`);
      return this.callsService.toggleManualCallRecording({ callId: callId, startRecording: startRecording });
   }

   @Action(MarkMaliciousCall)
   public markMaliciousCall(ctx: StateContext<ClientPhoneStateModel>, action: MarkMaliciousCall) {
      const userMail = this.store.selectSnapshot(SessionState.getAuthenticatedUserMail);
      const skillMail = this.store.selectSnapshot(SamwinSkillsState.getSkillConfigurationForCall)(action.callId)?.skillEmail;
      const mailConfig = this.store.selectSnapshot(SamwinMaliciousCallMailConfigState.getMaliciousCallMailConfig);

      let senderMail = this.store.selectSnapshot(CallTagsSelectors.getReplaceAllTagsInTextForActiveOrRingingCallOrAfterCallWorkFn)(
         !_.isEmpty(userMail) ? userMail : skillMail
      ).text;

      if (_.isEmpty(senderMail)) {
         senderMail = this.store.selectSnapshot(CallTagsSelectors.getReplaceAllTagsInTextForActiveOrRingingCallOrAfterCallWorkFn)(mailConfig.sender).text;
      }

      const recipient = this.store.selectSnapshot(CallTagsSelectors.getReplaceAllTagsInTextForActiveOrRingingCallOrAfterCallWorkFn)(mailConfig.receiver).text;

      const content = this.store.selectSnapshot(CallTagsSelectors.getReplaceAllTagsInTextForActiveOrRingingCallOrAfterCallWorkFn)(mailConfig.body).text;

      const subject = this.store.selectSnapshot(CallTagsSelectors.getReplaceAllTagsInTextForActiveOrRingingCallOrAfterCallWorkFn)(
         this.translateService.instant('samwin.configuration.malicious-call.message-subject')
      ).text;

      console.log('Mark malicious call');
      const result = this.callsService.markMaliciousCall({
         callId: action.callId,
         from: senderMail,
         to: recipient,
         subject: subject,
         body: content,
      });

      if (_.isEmpty(senderMail) || _.isEmpty(recipient)) {
         this.store.dispatch(
            new ToastUserInfoNotification(
               NotificationType.INFO,
               'notification.configuration.malicious_call_mail.incomplete.title',
               'notification.configuration.malicious_call_mail.incomplete.message'
            )
         );

         return null;
      }

      return result;
   }

   @Action(ToggleHoldLineA)
   public toggleHoldLineA(ctx: StateContext<ClientPhoneStateModel>, action: ToggleHoldLineA) {
      console.log('Toggle hold line A');
      return this.callsService.toggleHoldLineA();
   }

   @Action(ToggleHoldLineB)
   public toggleHoldLineB(ctx: StateContext<ClientPhoneStateModel>, action: ToggleHoldLineB) {
      console.log('Toggle hold line B');
      return this.callsService.toggleHoldLineB();
   }

   @Action(SwitchHoldLines)
   public switchHoldLines(ctx: StateContext<ClientPhoneStateModel>, action: SwitchHoldLines) {
      console.log('Switch hold lines');
      return this.callsService.switchHoldLines();
   }

   @Action(PhoneStartedRinging)
   public phoneStartedRinging(ctx: StateContext<ClientPhoneStateModel>, action: PhoneStartedRinging) {
      ctx.patchState({ ringingStartTime: moment() });
   }

   @Action(ChangeRequestUserDecision)
   public changeRequestUserDecision(ctx: StateContext<ClientPhoneStateModel>, action: ChangeRequestUserDecision) {
      ctx.patchState({ userDecisionRequested: action.newValue });
   }

   @Action(BargeInListen)
   public bargeInListen(ctx: StateContext<ClientPhoneStateModel>, action: BargeInListen) {
      const userId = this.store.selectSnapshot(SamwinUserAssistanceState.getUserToAssistFn)(action.userId);
      if (userId <= 0) {
         console.log('No valid user id selected to barge in (listen)');
         return;
      }

      // allow to stop supervising
      const currentState = this.store.selectSnapshot(SamwinUserAssistanceState.getMyAssistanceState)?.state ?? AssistanceState.None;
      if (currentState === AssistanceState.Listening) {
         return this.store.dispatch(new EndCallAction());
      }

      return this.store.dispatch(new UpdateDesiredAssistanceState(AssistanceState.Listening)).pipe(
         switchMap(() =>
            this.callsService.bargeInListen({
               userId: userId,
               callInitiatorId: ctx.getState().browserTabId,
            })
         )
      );
   }

   @Action(BargeInWhisper)
   public bargeInWhisper(ctx: StateContext<ClientPhoneStateModel>, action: BargeInWhisper) {
      const userId = this.store.selectSnapshot(SamwinUserAssistanceState.getUserToAssistFn)(action.userId);
      if (userId <= 0) {
         console.log('No valid user id selected to barge in (whisper)');
         return;
      }

      // allow to stop supervising
      const currentState = this.store.selectSnapshot(SamwinUserAssistanceState.getMyAssistanceState)?.state ?? AssistanceState.None;
      if (currentState === AssistanceState.Whispering) {
         return this.store.dispatch(new EndCallAction());
      }

      return this.store.dispatch(new UpdateDesiredAssistanceState(AssistanceState.Whispering)).pipe(
         switchMap(() =>
            this.callsService.bargeInWhisper({
               userId: userId,
               callInitiatorId: ctx.getState().browserTabId,
            })
         )
      );
   }

   @Action(BargeInConference)
   public bargeInConference(ctx: StateContext<ClientPhoneStateModel>, action: BargeInConference) {
      const userId = this.store.selectSnapshot(SamwinUserAssistanceState.getUserToAssistFn)(action.userId);
      if (userId <= 0) {
         console.log('No valid user id selected to barge in (conference)');
         return;
      }

      // allow to stop supervising
      const currentState = this.store.selectSnapshot(SamwinUserAssistanceState.getMyAssistanceState)?.state ?? AssistanceState.None;
      if (currentState === AssistanceState.Conference) {
         return this.store.dispatch(new EndCallAction());
      }

      return this.store.dispatch(new UpdateDesiredAssistanceState(AssistanceState.Conference)).pipe(
         switchMap(() =>
            this.callsService.bargeInConference({
               userId: userId,
               callInitiatorId: ctx.getState().browserTabId,
            })
         )
      );
   }

   @Action(SourceLineConnected)
   public SourceLineConnected(ctx: StateContext<ClientPhoneStateModel>, action: SourceLineConnected) {
      switch (action.lineStateEnum) {
         case LineStateEnum.IDLE:
            ctx.patchState({
               connectedTime: null,
            });
            break;
         case LineStateEnum.CONNECTED:
            if (!ctx.getState().connectedTime && action.lineStateEnum === LineStateEnum.CONNECTED) {
               ctx.patchState({
                  connectedTime: moment(),
               });
            }
            break;
      }
   }

   @Action(CallRecordingStateUpdatedByServer)
   public callRecordingStateUpdatedByServer(ctx: StateContext<ClientPhoneStateModel>, action: CallRecordingStateUpdatedByServer) {
      // ignoring the call id should be safe, as the core service updates the state for each single call
      if (ctx.getState().recordingState !== action.recordingState && action.recordingState === CallRecordingState.Error)
         this.store.dispatch(
            new ToastUserInfoNotification(
               NotificationType.ERROR,
               'notification.recording.recording_failure.title',
               'notification.recording.recording_failure.message'
            )
         );

      ctx.patchState({ recordingState: action.recordingState });
   }
}
